import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import moment from 'moment'
import { FaDownload } from 'react-icons/fa'

import Container from 'components/Container'
import Form from 'components/Form'
import { DateInput } from 'components/Form/date'
import { useToast } from 'hooks/toast'
import { useLoading } from 'hooks/loading'
import api from 'services/api'

import { CheckboxMultiSelect } from '../../ContractsUpdates/components/CheckboxMultiSelect'
import { breadcrumbList } from '../domain/breadcrumb/list'
import { namePageTitle } from '../domain/info'
import { toolsList } from '../domain/tools/list'
import { apiListInstitution, apiReport, apiReportPdf } from '../domain/api'
import {
  FilterContainer,
  MenuFoodBadge,
  ReportTable,
  SectionTitle,
  TableHeader
} from './styles'

type SummaryItem = {
  institution_id: number
  institution_name: string
  personalized_count: number
  not_personalized_count: number
  total: number
  personalized_percentage: number
}

type MenuFoodRow = {
  id: number
  name: string
  personalized: boolean
}

type StudentRow = {
  student_id: number
  student_name: string
  contract_id: number
  client_id: number
  client_name: string
  phone: string
  email: string
  menu_foods: MenuFoodRow[]
}

type ReportResponse = {
  items: SummaryItem[]
  totals: {
    personalized_count: number
    not_personalized_count: number
    total: number
    personalized_percentage: number
  }
  students?: {
    personalized: StudentRow[]
    not_personalized: StudentRow[]
  }
}

const parseQueryDate = (value: string | null): Date | undefined => {
  if (!value) {
    return undefined
  }
  const parsed = moment(value, 'YYYY-MM-DD', true)
  return parsed.isValid() ? parsed.startOf('day').toDate() : undefined
}

const formatPercentage = (value: number): string => `${value.toFixed(2)}%`

const MenuPersonalizationList = (): JSX.Element => {
  const { addToast } = useToast()
  const { activeLoading, disableLoading } = useLoading()
  const history = useHistory()
  const { search } = useLocation()
  const location = new URLSearchParams(search)

  const [startDate, setStartDate] = useState<Date | undefined>(() =>
    parseQueryDate(location.get('start_date'))
  )
  const [endDate, setEndDate] = useState<Date | undefined>(() =>
    parseQueryDate(location.get('end_date'))
  )
  const [institutionOptions, setInstitutionOptions] = useState<
    { value: number; label: string }[]
  >([])
  const [selectedInstitutionIds, setSelectedInstitutionIds] = useState<
    number[]
  >([])
  const [summaryItems, setSummaryItems] = useState<SummaryItem[]>([])
  const [totals, setTotals] = useState<ReportResponse['totals']>()
  const [studentLists, setStudentLists] = useState<ReportResponse['students']>()
  const [hasSearched, setHasSearched] = useState(false)
  const [issuedAt, setIssuedAt] = useState<string>()

  const reportPayload = useMemo(() => {
    if (!startDate || !endDate) {
      return null
    }
    return {
      start_date: moment(startDate).format('YYYY-MM-DD'),
      end_date: moment(endDate).format('YYYY-MM-DD'),
      institution_ids: selectedInstitutionIds.length
        ? selectedInstitutionIds
        : undefined
    }
  }, [startDate, endDate, selectedInstitutionIds])

  const formDefaultValues = useMemo(
    () => ({
      start_date: startDate,
      end_date: endDate
    }),
    [startDate, endDate]
  )

  const showStudentLists = selectedInstitutionIds.length === 1

  useEffect(() => {
    api
      .get(apiListInstitution())
      .then(res => {
        const ordered = res.data
          .filter((institution: IInstitutionData) => institution.is_active)
          .sort((a: IInstitutionData, b: IInstitutionData) =>
            a.company_social_name.localeCompare(b.company_social_name)
          )
        setInstitutionOptions(
          ordered.map((institution: IInstitutionData) => ({
            value: institution.id,
            label: institution.company_social_name
          }))
        )
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro ao carregar as instituições',
          description:
            'Houve um erro ao carregar as instituições disponíveis no banco de dados.'
        })
      })
  }, [addToast])

  const syncUrl = useCallback(() => {
    if (!reportPayload) {
      return
    }
    const query: string[] = [
      `start_date=${reportPayload.start_date}`,
      `end_date=${reportPayload.end_date}`
    ]
    if (reportPayload.institution_ids?.length) {
      query.push(`institution_ids=${reportPayload.institution_ids.join(',')}`)
    }
    history.replace(`/commercial/menuPersonalization?${query.join('&')}`)
  }, [history, reportPayload])

  const fetchReport = useCallback(async () => {
    if (!reportPayload) {
      addToast({
        type: 'error',
        title: 'Informe o período',
        description: 'Data inicial e data final são obrigatórias.'
      })
      return
    }
    activeLoading()
    try {
      const { data } = await api.post<ReportResponse>(
        apiReport(),
        reportPayload
      )
      setSummaryItems(data.items)
      setTotals(data.totals)
      setStudentLists(data.students)
      setHasSearched(true)
      setIssuedAt(moment().format('DD/MM/YYYY HH:mm:ss'))
      syncUrl()
    } catch {
      addToast({
        type: 'error',
        title: 'Erro ao carregar relatório',
        description: 'Não foi possível buscar a personalização de cardápio.'
      })
    } finally {
      disableLoading()
    }
  }, [activeLoading, addToast, disableLoading, reportPayload, syncUrl])

  const downloadPdf = async () => {
    if (!reportPayload) {
      return
    }
    activeLoading()
    try {
      const res = await api.post(apiReportPdf(), reportPayload, {
        responseType: 'arraybuffer'
      })
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `Relatorio-personalizacao-cardapio-${reportPayload.start_date}-${reportPayload.end_date}.pdf`
      anchor.click()
      window.URL.revokeObjectURL(url)
    } catch {
      addToast({
        type: 'error',
        title: 'Erro ao gerar PDF',
        description: 'Não foi possível baixar o relatório.'
      })
    } finally {
      disableLoading()
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(search)
    const start = params.get('start_date')
    const end = params.get('end_date')
    const institutions = params.get('institution_ids')

    if (start) {
      setStartDate(parseQueryDate(start))
    }
    if (end) {
      setEndDate(parseQueryDate(end))
    }
    if (institutions && institutionOptions.length) {
      const ids = institutions.split(',').map(Number)
      setSelectedInstitutionIds(ids)
    }
  }, [search, institutionOptions.length])

  const onSearch = () => {
    fetchReport()
  }

  const onClear = () => {
    setStartDate(undefined)
    setEndDate(undefined)
    setSelectedInstitutionIds([])
    setSummaryItems([])
    setTotals(undefined)
    setStudentLists(undefined)
    setHasSearched(false)
    setIssuedAt(undefined)
    history.replace('/commercial/menuPersonalization')
  }

  const renderStudentTable = (
    students: StudentRow[],
    emptyMessage: string
  ): JSX.Element => {
    if (!students.length) {
      return <p className="text-muted">{emptyMessage}</p>
    }

    return (
      <ReportTable>
        <thead>
          <tr>
            <th className="index-col">#</th>
            <th>Responsável</th>
            <th>Estudante</th>
            <th>Telefone</th>
            <th>E-mail</th>
            <th>Cardápios</th>
          </tr>
        </thead>
        <tbody>
          {students.map((student, index) => (
            <tr key={`${student.student_id}-${student.contract_id}`}>
              <td className="index-col">{index + 1}</td>
              <td>{student.client_name}</td>
              <td>
                <Link to={`/commercial/contracts/view/${student.contract_id}`}>
                  {student.student_name}
                </Link>
              </td>
              <td>{student.phone || '-'}</td>
              <td>{student.email || '-'}</td>
              <td>
                {student.menu_foods.length
                  ? student.menu_foods.map(menuFood => (
                      <MenuFoodBadge
                        key={menuFood.id}
                        personalized={menuFood.personalized}
                      >
                        {menuFood.name} ({menuFood.personalized ? 'Sim' : 'Não'}
                        )
                      </MenuFoodBadge>
                    ))
                  : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </ReportTable>
    )
  }

  const hasResults = (totals?.total ?? 0) > 0

  return (
    <Container
      pageTitle={namePageTitle}
      breadcrumb={breadcrumbList}
      tools={toolsList}
    >
      <FilterContainer>
        <Form
          defaultValues={formDefaultValues}
          onSubmit={onSearch}
          className="col-md-12"
        >
          <div className="col-md-12 d-flex flex-wrap">
            <div className="col-md-2 pe-8">
              <DateInput
                label="Data inicial"
                name="start_date"
                onChange={date => setStartDate(date)}
                selected={startDate}
                controlled
              />
            </div>
            <div className="col-md-2 pe-8">
              <DateInput
                label="Data final"
                name="end_date"
                onChange={date => setEndDate(date)}
                selected={endDate}
                controlled
              />
            </div>
            <div className="col-md-6 pe-8">
              <CheckboxMultiSelect
                label="Instituições (Selecione apenas uma para Relatório Detalhado)"
                fieldId="institution_ids"
                options={institutionOptions}
                selectedValues={selectedInstitutionIds}
                onChange={values =>
                  setSelectedInstitutionIds(values.map(Number))
                }
                placeholder="Todas"
              />
            </div>
          </div>
          <footer className="col-md-12 d-flex justify-content-end pb-5 gap-5">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={onClear}
            >
              LIMPAR
            </button>
            <button type="submit" className="btn btn-primary btn-sm">
              BUSCAR
            </button>
          </footer>
        </Form>
        <hr />
      </FilterContainer>

      {hasSearched && (
        <>
          <TableHeader>
            <div>
              <h3 className="h3 pt-6">Resumo por instituição (clientes)</h3>
              {issuedAt && (
                <p className="issued-at mb-0">Emissão: {issuedAt}</p>
              )}
            </div>
            <button
              type="button"
              className="btn"
              disabled={!hasResults}
              onClick={downloadPdf}
            >
              <FaDownload />
              Baixar relatório
            </button>
          </TableHeader>

          {!hasResults ? (
            <p className="text-muted">Nenhum registro encontrado no período.</p>
          ) : (
            <>
              <ReportTable>
                <thead>
                  <tr>
                    <th>Instituição</th>
                    <th>Clientes que personalizaram</th>
                    <th>Clientes que não personalizaram</th>
                    <th>Total de clientes</th>
                    <th>% de clientes</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryItems.map(item => (
                    <tr key={item.institution_id}>
                      <td>{item.institution_name}</td>
                      <td>{item.personalized_count}</td>
                      <td>{item.not_personalized_count}</td>
                      <td>{item.total}</td>
                      <td>{formatPercentage(item.personalized_percentage)}</td>
                    </tr>
                  ))}
                  {totals && (
                    <tr className="total-row">
                      <td>Total geral</td>
                      <td>{totals.personalized_count}</td>
                      <td>{totals.not_personalized_count}</td>
                      <td>{totals.total}</td>
                      <td>
                        {formatPercentage(totals.personalized_percentage)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </ReportTable>

              {showStudentLists && studentLists && (
                <>
                  <SectionTitle>Estudantes que personalizaram</SectionTitle>
                  {renderStudentTable(
                    studentLists.personalized,
                    'Nenhum estudante personalizou no período.'
                  )}

                  <SectionTitle>Estudantes que não personalizaram</SectionTitle>
                  {renderStudentTable(
                    studentLists.not_personalized,
                    'Todos os estudantes personalizaram no período.'
                  )}
                </>
              )}

              {selectedInstitutionIds.length > 1 && (
                <p className="text-muted mt-4">
                  Selecione apenas uma instituição para ver a lista de
                  estudantes.
                </p>
              )}
            </>
          )}
        </>
      )}
    </Container>
  )
}

export default MenuPersonalizationList
